import { MapPin, Users, HeartHandshake, Target, Award, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: HeartHandshake,
      title: 'Integrity',
      description: 'We provide honest, ethical advice with no false promises'
    },
    {
      icon: Users,
      title: 'Transparency',
      description: 'Clear communication and realistic expectations at every step'
    },
    {
      icon: Award,
      title: 'Professional Excellence',
      description: 'Expertise backed by deep knowledge of immigration policies'
    },
    {
      icon: Target,
      title: 'Client-First Approach',
      description: 'Your goals and aspirations drive our guidance'
    }
  ];

  const team = [
    {
      name: 'Immigration Consultants',
      description: 'Licensed professionals with years of experience in global visa processes'
    },
    {
      name: 'Documentation Experts',
      description: 'Specialists in preparing compliant and complete application packages'
    },
    {
      name: 'Client Support Team',
      description: 'Dedicated professionals ensuring smooth communication throughout'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              About Kiwi Connect Consultants
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Connecting you to global opportunities with expertise, ethics, and personalized care
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Kiwi Connect Consultants is a professional visas and immigration consultancy dedicated to helping individuals and families achieve their international travel, work, and settlement goals.
                </p>
                <p>
                  We are <strong>specialized in New Zealand visas</strong>, offering in-depth guidance aligned with the latest immigration policies, while also supporting clients for the UK, Europe, and other destinations.
                </p>
                <p>
                  Our approach is built on transparency, ethics, and personalized service. We understand that every immigration journey is unique, and we treat each case with the individual attention it deserves.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1573164574511-73c773193279"
                alt="Professional consultation"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-2 border-blue-100">
                <div className="flex items-center gap-3">
                  <Globe className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">10+</p>
                    <p className="text-sm text-gray-600">Countries Served</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission */}
          <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-12 mb-20 border-2 border-blue-100">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To provide ethical, transparent, and personalized immigration solutions that empower clients to make informed decisions about their future. We aim to simplify complex visa processes while maintaining the highest standards of professional integrity.
                </p>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Our Core Values
              </h2>
              <p className="text-lg text-gray-600">
                The principles that guide every decision we make
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, idx) => (
                <Card key={idx} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <value.icon className="h-7 w-7 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Specialization Highlight */}
          <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-12 text-white mb-20">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="h-10 w-10" />
                  <h2 className="text-3xl font-bold">New Zealand Visa Specialists</h2>
                </div>
                <p className="text-lg text-blue-100 leading-relaxed mb-6">
                  New Zealand immigration is our core expertise. We stay updated with the latest policy changes, pathway options, and documentation requirements specific to NZ visas.
                </p>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold mb-2">🏢 Head Office:</p>
                  <p className="text-sm">{contactInfo.addresses.headOffice}</p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Visitor & Tourist Visas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Work Visa Guidance</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Residence Pathway Assessment</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Documentation & Compliance Support</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
                  alt="New Zealand cityscape"
                  className="rounded-xl shadow-2xl w-full h-[300px] object-cover"
                />
              </div>
            </div>
          </div>

          {/* Team */}
          <div>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Our Expert Team
              </h2>
              <p className="text-lg text-gray-600">
                Professionals dedicated to your success
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {team.map((member, idx) => (
                <Card key={idx} className="border-2 border-gray-200 hover:border-blue-300 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-gray-600 text-sm">{member.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Let's discuss how we can help you achieve your immigration goals
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+919998581146"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Schedule Free Consultation
            </a>
            <a
              href="/eligibility"
              className="inline-flex items-center gap-2 bg-white border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all"
            >
              Check Eligibility
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
